import { useCallback, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  DsButton,
  DsInput,
  DsTabNav,
  type DsTab,
} from '@/modules/internal-v2/ds';
import { PERMISSIONS } from '@/constants/permissions';
import { usePermissions } from '@/hooks/use-permissions';
import {
  useApprove,
  useApproveBranch,
  useBulkApprove,
  useExtract,
  useExtractionJobs,
  useReject,
  useReviewQueue,
} from '@/api/specifications/specifications.hooks';
import type {
  BulkApproveRequest,
  ResourceType,
  ReviewIdentityNode,
  ReviewVocabularyEntry,
} from '@/api/specifications/specifications.types';
import { ManufacturerPicker } from '../../shared/manufacturer-picker.component';
import { IdentitySection } from './components/identity-section.component';
import { ImportsSection } from './components/imports-section.component';
import { VocabularySection } from './components/vocabulary-section.component';
import { SelectionBar } from './components/selection-bar.component';
import { ExtractionStatus } from './components/extraction-status.component';
import { MergeModal } from './components/merge-modal.component';
import {
  parseSelectionKey,
  type MergeTarget,
  type SelectedItem,
} from './components/selection.types';
import './specifications.css';

type TabId = 'identity' | 'vocabulary' | 'imports';

export function SpecificationsPage() {
  const { has } = usePermissions();
  const canManage = has(PERMISSIONS.SPECIFICATIONS_MANAGE);
  const { enqueueSnackbar } = useSnackbar();

  // ── Filters ────────────────────────────────────────────────────
  const [manufacturerId, setManufacturerId] = useState<number | undefined>();
  const [search, setSearch] = useState('');
  const [minItems, setMinItems] = useState(1);
  const [activeTab, setActiveTab] = useState<TabId>('identity');

  const filters = useMemo(
    () => ({ manufacturerId, search: search || undefined, minItems }),
    [manufacturerId, search, minItems],
  );

  // ── Data ───────────────────────────────────────────────────────
  const { data: jobs } = useExtractionJobs('active');
  const activeJobCount = jobs?.jobs.length ?? 0;

  const { data, isLoading } = useReviewQueue(filters, {
    refetchInterval: activeJobCount > 0 ? 10_000 : false,
  });

  const approve = useApprove();
  const approveBranch = useApproveBranch();
  const reject = useReject();
  const bulkApprove = useBulkApprove();
  const extract = useExtract();

  // ── Selection (shared across both tabs) ────────────────────────
  const [selectionSet, setSelectionSet] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const selectionList: SelectedItem[] = useMemo(
    () => Array.from(selectionSet, parseSelectionKey),
    [selectionSet],
  );

  const handleSelectChange = useCallback((key: string, next: boolean) => {
    setSelectionSet((prev) => {
      const out = new Set(prev);
      if (next) out.add(key);
      else out.delete(key);
      return out;
    });
  }, []);

  const handleSelectMany = useCallback(
    (entries: { key: string; selected: boolean }[]) => {
      setSelectionSet((prev) => {
        const out = new Set(prev);
        for (const { key, selected } of entries) {
          if (selected) out.add(key);
          else out.delete(key);
        }
        return out;
      });
    },
    [],
  );

  const clearSelection = useCallback(() => setSelectionSet(new Set()), []);

  // Reject-eligibility needs item counts — index everything from the queue.
  const itemCountByKey = useMemo(
    () => indexItemCounts(data?.identity ?? [], data?.vocabulary ?? {}),
    [data],
  );
  const rejectEligibleCount = selectionList.filter(
    (it) => (itemCountByKey.get(`${it.resource}:${it.id}`) ?? 0) === 0,
  ).length;

  // ── Mutations ──────────────────────────────────────────────────
  const callApprove = (resource: ResourceType, id: number) =>
    approve.mutate(
      { resource, id },
      {
        onSuccess: () => enqueueSnackbar('Freigegeben', { variant: 'success' }),
        onError: (e) =>
          enqueueSnackbar(`Freigabe fehlgeschlagen: ${(e as Error).message}`, {
            variant: 'error',
          }),
      },
    );

  const callReject = (resource: ResourceType, id: number) => {
    if (!confirm('Vorschlag ablehnen (löschen)? Kann nicht rückgängig gemacht werden.')) return;
    reject.mutate(
      { resource, id },
      {
        onSuccess: () => enqueueSnackbar('Abgelehnt', { variant: 'success' }),
        onError: (e) =>
          enqueueSnackbar(`Ablehnen fehlgeschlagen: ${(e as Error).message}`, {
            variant: 'error',
          }),
      },
    );
  };

  const callApproveBranch = (productLineId: number) =>
    approveBranch.mutate(productLineId, {
      onSuccess: () => enqueueSnackbar('Branch freigegeben', { variant: 'success' }),
      onError: (e) =>
        enqueueSnackbar(`Branch-Freigabe fehlgeschlagen: ${(e as Error).message}`, {
          variant: 'error',
        }),
    });

  const handleBulkApprove = () => {
    if (selectionList.length === 0) return;
    const body: BulkApproveRequest = {
      productLineIds: selectionList.filter((s) => s.resource === 'productLine').map((s) => s.id),
      modelIds: selectionList.filter((s) => s.resource === 'model').map((s) => s.id),
      vocabularyIds: selectionList.filter((s) => s.resource === 'vocabulary').map((s) => s.id),
    };
    bulkApprove.mutate(body, {
      onSuccess: (results) => {
        const ok = results.filter((r) => r.ok).length;
        const fail = results.length - ok;
        if (fail === 0) {
          enqueueSnackbar(`${ok} freigegeben`, { variant: 'success' });
          clearSelection();
        } else {
          enqueueSnackbar(`${ok} freigegeben, ${fail} fehlgeschlagen`, {
            variant: 'warning',
          });
          // Drop only the ones that succeeded; keep failures selected.
          setSelectionSet((prev) => {
            const out = new Set(prev);
            for (const r of results) {
              if (r.ok) out.delete(`${r.resource}:${r.id}`);
            }
            return out;
          });
        }
      },
      onError: (e) =>
        enqueueSnackbar(`Sammelfreigabe fehlgeschlagen: ${(e as Error).message}`, {
          variant: 'error',
        }),
    });
  };

  const handleBulkReject = async () => {
    const eligible = selectionList.filter(
      (it) => (itemCountByKey.get(`${it.resource}:${it.id}`) ?? 0) === 0,
    );
    if (eligible.length === 0) return;
    if (
      !confirm(
        `${eligible.length} Vorschläge ablehnen (löschen)? Kann nicht rückgängig gemacht werden.`,
      )
    )
      return;
    let ok = 0;
    let failed = 0;
    for (const it of eligible) {
      try {
        await reject.mutateAsync({ resource: it.resource, id: it.id });
        ok++;
      } catch {
        failed++;
      }
    }
    if (failed === 0) {
      enqueueSnackbar(`${ok} abgelehnt`, { variant: 'success' });
      clearSelection();
    } else {
      enqueueSnackbar(`${ok} abgelehnt, ${failed} fehlgeschlagen`, {
        variant: 'warning',
      });
    }
  };

  const callExtract = () => {
    if (!manufacturerId) {
      enqueueSnackbar('Bitte zuerst einen Hersteller wählen', { variant: 'warning' });
      return;
    }
    extract.mutate(
      { manufacturerId },
      {
        onSuccess: (res) => {
          if (res.groupsEnqueued === 0) {
            enqueueSnackbar(
              'Keine Artikel in unseren vier Kategorien (Smartphone / Tablet / Smartwatch / Notebook)',
              { variant: 'warning' },
            );
            return;
          }
          enqueueSnackbar(`${res.groupsEnqueued} Extraktion(en) gestartet`, {
            variant: 'success',
          });
        },
        onError: (e) =>
          enqueueSnackbar(`Extraktion fehlgeschlagen: ${(e as Error).message}`, {
            variant: 'error',
          }),
      },
    );
  };

  // ── Merge modal ────────────────────────────────────────────────
  const [merge, setMerge] = useState<MergeTarget | null>(null);

  // ── Tabs ───────────────────────────────────────────────────────
  const totals = data?.totals ?? { productLines: 0, models: 0, vocabulary: 0 };
  const tabs: DsTab[] = [
    { id: 'identity', label: 'Identität', count: totals.productLines + totals.models },
    { id: 'vocabulary', label: 'Vokabular', count: totals.vocabulary },
    { id: 'imports', label: 'Imports' },
  ];

  return (
    <div className="sp-page">
      {/* ── Toolbar ── */}
      <div className="sp-toolbar">
        <div className="sp-toolbar-inner">
          <ManufacturerPicker
            value={manufacturerId ?? null}
            onChange={(v) => setManufacturerId(v ?? undefined)}
            className="sp-filter-mfr"
          />
          <DsInput
            className="sp-filter-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Suche…"
          />
          <DsInput
            className="sp-filter-min"
            type="number"
            min={1}
            value={String(minItems)}
            onChange={(e) => setMinItems(Math.max(1, parseInt(e.target.value, 10) || 1))}
            placeholder="Min. Artikel"
            title="Vorschläge mit weniger Artikeln ausblenden"
          />

          <div className="sp-toolbar-spacer" />

          <ExtractionStatus count={activeJobCount} />
          {canManage && (
            <DsButton
              variant="primary"
              onClick={callExtract}
              disabled={extract.isPending || !manufacturerId}
              title={
                !manufacturerId
                  ? 'Hersteller wählen, um Extraktion zu starten'
                  : 'Extraktion für den gewählten Hersteller starten'
              }
            >
              {extract.isPending ? 'Starte…' : 'Extraktion starten'}
            </DsButton>
          )}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="sp-tabs">
        <DsTabNav
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={(id) => setActiveTab(id as TabId)}
          maxWidth={1280}
        />
      </div>

      {/* ── Body ── */}
      <div className="sp-body ds-scroll">
        <div className="sp-body-inner">
          {activeTab === 'identity' && (
            <IdentitySection
              nodes={data?.identity ?? []}
              isLoading={isLoading}
              canManage={canManage}
              selection={selectionSet}
              onSelectChange={handleSelectChange}
              onSelectMany={handleSelectMany}
              onApprove={callApprove}
              onReject={callReject}
              onApproveBranch={callApproveBranch}
              onOpenMerge={setMerge}
            />
          )}
          {activeTab === 'vocabulary' && (
            <VocabularySection
              groups={data?.vocabulary ?? {}}
              isLoading={isLoading}
              canManage={canManage}
              selection={selectionSet}
              onSelectChange={handleSelectChange}
              onSelectMany={handleSelectMany}
              onApprove={callApprove}
              onReject={callReject}
              onOpenMerge={setMerge}
            />
          )}
          {activeTab === 'imports' && <ImportsSection canManage={canManage} />}
        </div>
      </div>

      {/* ── Sticky bulk action bar (queue tabs only) ── */}
      {canManage && activeTab !== 'imports' && (
        <SelectionBar
          selected={selectionList}
          rejectEligibleCount={rejectEligibleCount}
          onApproveAll={handleBulkApprove}
          onRejectAll={handleBulkReject}
          onClear={clearSelection}
          approveBusy={bulkApprove.isPending}
          rejectBusy={reject.isPending}
        />
      )}

      {merge && (
        <MergeModal
          open
          resource={merge.resource}
          sourceId={merge.sourceId}
          sourceLabel={merge.sourceLabel}
          scope={merge.scope}
          onClose={() => setMerge(null)}
        />
      )}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────

function indexItemCounts(
  identity: ReviewIdentityNode[],
  vocab: Record<string, ReviewVocabularyEntry[]>,
): Map<string, number> {
  const out = new Map<string, number>();
  for (const n of identity) {
    out.set(`productLine:${n.productLine.id}`, n.productLine.itemCount);
    for (const m of n.modelsNeedingReview) {
      out.set(`model:${m.id}`, m.itemCount);
    }
  }
  for (const list of Object.values(vocab)) {
    for (const v of list) out.set(`vocabulary:${v.id}`, v.itemCount);
  }
  return out;
}
